import * as React from "react"

function assignRef<T>(ref: React.Ref<T> | undefined, node: T | null) {
  if (typeof ref === "function") {
    ref(node)
  } else if (ref) {
    ref.current = node
  }
}

function hasText(value: string | null | undefined): boolean {
  return Boolean(value?.trim())
}

/** Roles that only say "image", which still needs a name to mean anything. */
const IMAGE_ROLES = new Set([
  "img",
  "image",
  "graphics-document",
  "graphics-symbol",
])

/** Whether the svg sets a role beyond "image", such as `status`. */
function hasOwnRole(svg: Element): boolean {
  const role = svg.getAttribute("role")?.trim()
  return Boolean(role) && !IMAGE_ROLES.has(role as string)
}

/**
 * Whether the svg carries a non-empty name, visible text, or semantics of its
 * own, such as a `role="status"` spinner or a live region.
 */
function isNamed(svg: Element): boolean {
  return (
    hasOwnRole(svg) ||
    svg.hasAttribute("aria-live") ||
    hasText(svg.getAttribute("aria-label")) ||
    // A reference is the caller's chosen name even before its target renders,
    // and the hook does not watch the target, so it is not resolved here.
    hasText(svg.getAttribute("aria-labelledby")) ||
    Array.from(svg.children).some(
      (child) => child.localName === "title" && hasText(child.textContent),
    ) ||
    Array.from(svg.querySelectorAll("text")).some((text) =>
      hasText(text.textContent),
    )
  )
}

/** Whether a button or link carries a name of its own. */
function isNamedControl(control: Element): boolean {
  return (
    hasText(control.getAttribute("aria-label")) ||
    hasText(control.getAttribute("aria-labelledby")) ||
    hasText(control.textContent)
  )
}

/**
 * The svgs to reconcile: those directly inside the element, plus, with
 * `inNamedControls`, those directly inside a named button or link that is a
 * direct child, such as a show-password toggle in an input adornment.
 */
function candidateSvgs(node: Element, inNamedControls: boolean): Element[] {
  const children = Array.from(node.children)
  const svgs = children.filter((child) => child.localName === "svg")
  if (inNamedControls) {
    for (const control of children) {
      if (
        (control.localName === "button" || control.localName === "a") &&
        isNamedControl(control)
      ) {
        svgs.push(
          ...Array.from(control.children).filter(
            (child) => child.localName === "svg",
          ),
        )
      }
    }
  }
  return svgs
}

/** Attributes whose change can name an svg or hand `aria-hidden` to the caller. */
const WATCHED_ATTRIBUTES = [
  "aria-hidden",
  "aria-label",
  "aria-labelledby",
  "role",
  "aria-live",
]

/**
 * Hides each unnamed `<svg>` directly inside an element from assistive tech.
 * lucide draws its icons as bare `<svg>`s, which Chrome exposes as images with
 * no name. Beside a control's words or `aria-label` the glyph adds nothing, so
 * it is marked `aria-hidden`. An `<svg>` named by `aria-label`,
 * `aria-labelledby`, or a `<title>` child stays exposed, and one that sets
 * `aria-hidden` itself keeps what it set.
 *
 * Only the svg is touched, never a sibling element, so text or a status a
 * caller slots beside the icon still reads. Runs after every commit, since a
 * re-render can swap in a different icon or give an icon a name, and again on
 * any DOM change inside the element, since an icon component can re-render on
 * its own. React does not track the attribute this writes, so an svg that
 * gains a name after it was hidden has the attribute taken back off, unless
 * the caller has since set `aria-hidden` itself.
 *
 * `inNamedControls` also reaches an icon inside a named button or link the
 * caller slots in, for slots that hold a small control rather than an icon.
 * With `enabled` false the hook hides nothing and restores any svg it hid.
 */
export function useDecorativeSvgs<T extends HTMLElement>(
  forwardedRef?: React.Ref<T>,
  {
    inNamedControls = false,
    enabled = true,
  }: { inNamedControls?: boolean; enabled?: boolean } = {},
): (node: T | null) => void {
  const nodeRef = React.useRef<T | null>(null)
  const hiddenByHook = React.useRef(new WeakSet<Element>())
  const observerRef = React.useRef<{
    node: T
    observer: MutationObserver
  } | null>(null)
  // The observer outlives a render, so it calls the latest pass, which reads
  // this render's options rather than the ones it was created under.
  const latestPass = React.useRef<(records: MutationRecord[]) => void>(
    () => undefined,
  )

  React.useLayoutEffect(() => {
    const node = nodeRef.current
    const hidden = hiddenByHook.current

    // A write to `aria-hidden` the hook did not make came from the caller,
    // whenever it lands, so the caller now owns that attribute.
    const takeCallerWrites = (records: MutationRecord[]) => {
      for (const record of records) {
        if (record.attributeName === "aria-hidden") {
          hidden.delete(record.target as Element)
        }
      }
    }

    const reconcile = (observer: MutationObserver | undefined) => {
      for (const svg of node ? candidateSvgs(node, inNamedControls) : []) {
        if (hidden.has(svg)) {
          if (!enabled || isNamed(svg)) {
            svg.removeAttribute("aria-hidden")
            hidden.delete(svg)
          }
        } else if (
          enabled &&
          !svg.hasAttribute("aria-hidden") &&
          !isNamed(svg)
        ) {
          svg.setAttribute("aria-hidden", "true")
          hidden.add(svg)
        }
      }
      // Drop the records of this pass's own writes before the callback sees
      // them.
      observer?.takeRecords()
    }

    latestPass.current = (records) => {
      takeCallerWrites(records)
      reconcile(observerRef.current?.observer)
    }

    if (observerRef.current?.node !== node) {
      observerRef.current?.observer.disconnect()
      observerRef.current = null
      if (node && typeof MutationObserver !== "undefined") {
        const observer = new MutationObserver((records) =>
          latestPass.current(records),
        )
        observer.observe(node, {
          subtree: true,
          childList: true,
          characterData: true,
          attributes: true,
          attributeFilter: WATCHED_ATTRIBUTES,
        })
        observerRef.current = { node, observer }
      }
    }
    // Writes earlier in this commit have not reached the callback yet.
    latestPass.current(observerRef.current?.observer.takeRecords() ?? [])
  })

  React.useLayoutEffect(
    () => () => {
      observerRef.current?.observer.disconnect()
      observerRef.current = null
    },
    [],
  )

  return React.useCallback(
    (node: T | null) => {
      nodeRef.current = node
      assignRef(forwardedRef, node)
    },
    [forwardedRef],
  )
}
